import { LogOut, RotateCw, Users, User, ArrowLeft, HelpCircle } from 'lucide-react';
import { VersionInfo } from './VersionInfo';

interface HeaderProps {
  userName?: string;
  userProfilePicture?: string;
  onSignOut?: () => void;
  onOpenFriends?: () => void;
  onOpenProfile?: () => void;
  onOpenHelp?: () => void;
  pendingRequestsCount?: number;
  viewingFriendName?: string;
  onBackToMyGlobe?: () => void;
}

export function Header({ userName, userProfilePicture, onSignOut, onOpenFriends, onOpenProfile, onOpenHelp, pendingRequestsCount, viewingFriendName, onBackToMyGlobe }: HeaderProps) {
  return (
    <header className="absolute top-0 left-0 right-0 z-20 p-2 md:p-4 safe-area-top">
      {viewingFriendName ? (
        /* Viewing Friend's Globe - Centered Layout */
        <div className="flex items-center gap-2 md:gap-4 justify-center">
          {onBackToMyGlobe && (
            <button
              onClick={onBackToMyGlobe}
              className="btn-ghost p-2"
              title="Back to my globe"
            >
              <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          )}
          <div className="text-center">
            <h1 className="text-lg md:text-3xl font-bold gradient-text">Viewing {viewingFriendName}'s Globe</h1>
            <p className="text-xs md:text-sm text-white/60 tracking-widest uppercase mt-1 hidden md:block">
              Explore their flight journey
            </p>
          </div>
        </div>
      ) : (
        /* Normal Layout */
        <div className="flex items-center justify-between gap-2">
          {/* Logo */}
          <div className="text-left flex-1">
            <h1 className="text-base md:text-2xl font-bold gradient-text">✈️ Flight Tracker</h1>
            <p className="text-xs text-white/60 tracking-widest uppercase hidden md:block">
              Track your journeys around the world
            </p>
          </div>

          {/* User info & actions */}
          <div className="flex items-center gap-2 md:gap-4">
          {userName && onOpenProfile && (
            <button
              onClick={onOpenProfile}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors btn-ghost p-2"
              title="Profile Settings"
            >
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden border border-white/20">
                {userProfilePicture ? (
                  <img src={userProfilePicture} alt={userName} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-4 h-4" />
                )}
              </div>
              <span className="hidden lg:inline">{userName}</span>
            </button>
          )}
          {onOpenFriends && (
            <button
              onClick={onOpenFriends}
              className="btn-ghost flex items-center gap-2 relative p-2"
              title="Friends"
            >
              <Users className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden lg:inline">Friends</span>
              {pendingRequestsCount !== undefined && pendingRequestsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-semibold shadow-lg">
                  {pendingRequestsCount}
                </span>
              )}
            </button>
          )}
          {onOpenHelp && (
            <button
              onClick={onOpenHelp}
              className="btn-ghost flex items-center gap-2 p-2"
              title="Help & User Guides"
            >
              <HelpCircle className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden lg:inline">Help</span>
            </button>
          )}
          {onSignOut && (
            <button
              onClick={onSignOut}
              className="btn-ghost flex items-center gap-2 p-2"
              title="Sign out"
            >
              <LogOut className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden lg:inline">Sign Out</span>
            </button>
          )}
        </div>
        </div>
      )}
    </header>
  );
}

interface GlobeControlsProps {
  autoRotate: boolean;
  onToggleRotate: () => void;
}
export function GlobeControls({
  autoRotate,
  onToggleRotate,
}: GlobeControlsProps) {
  return (
    <div className="absolute bottom-16 md:bottom-20 left-1/2 -translate-x-1/2 z-20 flex gap-2 md:gap-3">
      <button
        onClick={onToggleRotate}
        className={`btn-secondary flex items-center gap-2 text-sm md:text-base px-3 md:px-6 py-2 md:py-3 ${
          autoRotate ? 'bg-primary-500/20 border-primary-500/50' : ''
        }`}
      >
        <RotateCw className={`w-4 h-4 ${autoRotate ? 'animate-spin-slow' : ''}`} />
        <span className="hidden sm:inline">Auto Rotate</span>
      </button>
    </div>
  );
}

export function Instructions() {
  return (
    <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1">
      <p className="text-xs md:text-sm text-white/50 text-center px-4">
        <span className="hidden sm:inline">🖱️ Drag to rotate • Scroll to zoom</span>
        <span className="sm:hidden">👆 Touch to interact</span>
      </p>
      <VersionInfo className="text-center" />
    </div>
  );
}
